import {AnalyticsProvider} from '@github-ui/analytics-provider'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {FilteredListPageLayout} from '@github-ui/layout-primitives/FilteredListPageLayout'
import {newRepoPath} from '@github-ui/paths'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {ReposFilter} from '@github-ui/repos-filter'
import {Button, PageHeader, PageLayout} from '@primer/react'
import {useEffect, useState} from 'react'

import {OrgReposListContent} from '../components/OrgReposListContent'
import {getOrderedTypes, getTypeFromQuery, OrgSidebar, OrgSidebarCollapsedButton} from '../components/OrgSidebar'
import {useListResults} from '../hooks/use-list-results'
import type {PagePayload} from '../types'
import {orgReposPageRoute} from './org-repos-page-route'
import styles from './OrgReposPage.module.css'

export function OrgReposPageForDataRouter() {
  const {data} = useRouteQuery(orgReposPageRoute, 'mainQuery')
  const {org} = useRouteParams(orgReposPageRoute)

  return (
    <AnalyticsProvider appName="org-repos-page" category="Org Repos page" metadata={{page: 'org-repos'}}>
      <OrgReposPage payload={data} org={org} />
    </AnalyticsProvider>
  )
}

interface OrgReposPageProps {
  payload: PagePayload
  org?: string
}

export function OrgReposPage({payload, org}: OrgReposPageProps) {
  const {userInfo, definitions, namespaces, typeFilters, searchable, additionalFilterProviders} = payload

  const {fetchedQuery, fetchResults} = useListResults()
  const [query, setQuery] = useState(fetchedQuery)

  /* eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags --
     TODO: Migrate to isFeatureEnabled from @github-ui/feature-flags. See
     https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
  const showFilterDialog = useFeatureFlag('repos_list_show_filter_dialog')

  const showUserRelativeFilters = userInfo !== undefined

  const orderedTypes = getOrderedTypes(typeFilters, showUserRelativeFilters)
  const fetchedType = getTypeFromQuery(fetchedQuery, orderedTypes)

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setQuery(fetchedQuery)
  }, [fetchedQuery])

  const {canCreateRepository} = userInfo || {}

  function updateQuery(q: string) {
    setQuery(q)
  }

  function updateAndSubmitQuery(q: string) {
    updateQuery(q)
    fetchResults({q})
  }

  const listTitle = orderedTypes.find(({id}) => id === fetchedType)?.text || 'All'

  const reposFilter = searchable ? (
    <ReposFilter
      id="repos-list-filter"
      label="Search repositories"
      variant={showFilterDialog ? 'full' : 'input'}
      placeholder="Search repositories"
      filterValue={query}
      definitions={definitions}
      namespaces={namespaces}
      options={{
        withFinderTerms: showUserRelativeFilters,
        withRelevanceSort: true,
        additionalFilterProviders,
      }}
      onChange={updateQuery}
      onSubmit={filterQuery => fetchResults({q: filterQuery.raw})}
    />
  ) : null

  if (isFeatureEnabled('org_repos_filtered_list_layout')) {
    return (
      <FilteredListPageLayout storageKey="org-repos-sidebar">
        <FilteredListPageLayout.Header>
          <FilteredListPageLayout.PageHeader>
            <PageHeader.TitleArea>
              <PageHeader.Title as="h1">{listTitle}</PageHeader.Title>
            </PageHeader.TitleArea>
            {canCreateRepository && (
              <PageHeader.Actions>
                <FilteredListPageLayout.HeaderActions
                  menuAriaLabel="More repository actions"
                  actions={[
                    {
                      key: 'new-repository',
                      label: 'New repository',
                      primary: true,
                      href: newRepoPath({org}),
                    },
                  ]}
                />
              </PageHeader.Actions>
            )}
          </FilteredListPageLayout.PageHeader>
        </FilteredListPageLayout.Header>
        <FilteredListPageLayout.Sidebar aria-label="Repository filters" dialogTitle="Repository filters">
          <OrgSidebar
            type={fetchedType}
            onQueryChanged={updateAndSubmitQuery}
            types={orderedTypes}
            useCollapsibleLayout
          />
        </FilteredListPageLayout.Sidebar>
        <FilteredListPageLayout.Content>
          {reposFilter && (
            <FilteredListPageLayout.FilterBar aria-label="Search repositories">
              {reposFilter}
            </FilteredListPageLayout.FilterBar>
          )}
          <FilteredListPageLayout.Results aria-label={listTitle}>
            <OrgReposListContent payload={payload} onQuerySubmit={updateAndSubmitQuery} />
          </FilteredListPageLayout.Results>
        </FilteredListPageLayout.Content>
      </FilteredListPageLayout>
    )
  }

  return (
    <PageLayout containerWidth="full" columnGap="none" padding="none">
      <PageLayout.Pane
        divider="line"
        hidden={{narrow: true, regular: false, wide: false}}
        padding="condensed"
        position="start"
        className={styles.pageLayoutPane}
      >
        <aside>
          <OrgSidebar type={fetchedType} onQueryChanged={updateAndSubmitQuery} types={orderedTypes} />
        </aside>
      </PageLayout.Pane>
      {/* setting px on top of the padding prop is required to visually compensate padding of the list items in the sidebar */}
      <PageLayout.Content padding="condensed" className={styles.orgReposPageContent}>
        {searchable && (
          <>
            <div className={styles.pageHeaderContainer}>
              <div className={styles.mobileNavContainer}>
                <OrgSidebarCollapsedButton
                  type={fetchedType}
                  onQueryChanged={updateAndSubmitQuery}
                  types={orderedTypes}
                />
              </div>
              <h1 className={styles.pageTitle}>{listTitle}</h1>
              <div className={styles.actionButtonsContainer}>
                {canCreateRepository && (
                  <Button as="a" href={newRepoPath({org})} variant="primary" size="medium">
                    New repository
                  </Button>
                )}
              </div>
            </div>
            {reposFilter}
          </>
        )}

        <OrgReposListContent payload={payload} onQuerySubmit={updateAndSubmitQuery} />
      </PageLayout.Content>
    </PageLayout>
  )
}
